/**
 * Pure camera math for the rust overlay's full-viewport (Figma-style) canvas.
 *
 * The camera lives in book/print px (the model's native space) so `zoom` is a drop-in for the old
 * `stage.scale` (CSS screen px per book px). `originX/originY` is the book point at the host's top-left.
 * The render layer (`render_camera`) works in scene px (book × renderScale) and device px (CSS × dpr);
 * `cameraRenderArgs` converts. Mirrors `core/camera.rs` semantics.
 */

import type { Point } from './overlay-transform';

export interface Camera {
  /** Book/print x at the host's top-left edge. */
  originX: number;
  originY: number;
  /** CSS screen px per book px (same units as the old stage.scale). */
  zoom: number;
}

/** Args for `SceneRenderer.render_camera` — scene-space origin/zoom + device-px output buffer. */
export interface CameraRenderArgs {
  originX: number;
  originY: number;
  zoom: number;
  outW: number;
  outH: number;
}

/** Book point → CSS px relative to the host's top-left. */
export function bookToScreen(cam: Camera, p: Point): Point {
  return { x: (p.x - cam.originX) * cam.zoom, y: (p.y - cam.originY) * cam.zoom };
}

/** CSS px relative to the host's top-left → book point. */
export function screenToBook(cam: Camera, sx: number, sy: number): Point {
  return { x: sx / cam.zoom + cam.originX, y: sy / cam.zoom + cam.originY };
}

/**
 * Convert the book-space camera to `render_camera` args. The scene JSON is built at `renderScale`
 * (scene = book × renderScale); the output buffer is device px (CSS × dpr).
 */
export function cameraRenderArgs(
  cam: Camera,
  renderScale: number,
  hostCssW: number,
  hostCssH: number,
  dpr: number,
): CameraRenderArgs {
  return {
    originX: cam.originX * renderScale,
    originY: cam.originY * renderScale,
    zoom: (cam.zoom * dpr) / renderScale,
    outW: Math.max(1, Math.round(hostCssW * dpr)),
    outH: Math.max(1, Math.round(hostCssH * dpr)),
  };
}

/** Spread size in scene px. Floor, never round: drawables scale by the raw factor, so a rounded-up buffer
 *  keeps a transparent strip at the right/bottom edge and the canvas's paper-white shows through it. */
export function spreadBufferSize(
  viewport: { width: number; height: number },
  renderScale: number,
): { width: number; height: number } {
  return {
    width: Math.max(1, Math.floor(viewport.width * renderScale)),
    height: Math.max(1, Math.floor(viewport.height * renderScale)),
  };
}

/** A camera that fits the spread into the host (centered), fitting by the tighter axis. */
export function fitCamera(
  viewport: { width: number; height: number },
  hostCssW: number,
  hostCssH: number,
  padding: number | { x: number; y: number } = 0,
): Camera {
  return fitCameraToBox({ x: 0, y: 0, width: viewport.width, height: viewport.height }, hostCssW, hostCssH, padding);
}

/** Same fit, for a box that is not the whole scene (e.g. the printed page inside the pre-trim sheet).
 *  Padding is the air left around the box — a scalar keeps both axes equal, `{x, y}` sets them apart. */
export function fitCameraToBox(
  box: { x: number; y: number; width: number; height: number },
  hostCssW: number,
  hostCssH: number,
  padding: number | { x: number; y: number } = 0,
): Camera {
  const pad = typeof padding === 'number' ? { x: padding, y: padding } : padding;
  const availW = Math.max(1, hostCssW - 2 * pad.x);
  const availH = Math.max(1, hostCssH - 2 * pad.y);
  const w = Math.max(1, box.width);
  const h = Math.max(1, box.height);
  const zoom = Math.max(1e-6, Math.min(availW / w, availH / h));
  // Center: the box's center book point maps to the host's center.
  return {
    originX: box.x + w / 2 - hostCssW / (2 * zoom),
    originY: box.y + h / 2 - hostCssH / (2 * zoom),
    zoom,
  };
}

/** Change zoom while keeping the book point under the screen anchor fixed (wheel/pinch around cursor). */
export function zoomAround(cam: Camera, anchorScreenX: number, anchorScreenY: number, newZoom: number): Camera {
  const world = screenToBook(cam, anchorScreenX, anchorScreenY);
  return {
    originX: world.x - anchorScreenX / newZoom,
    originY: world.y - anchorScreenY / newZoom,
    zoom: newZoom,
  };
}

export function clampZoom(zoom: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, zoom));
}

export interface ResolutionCaps {
  baseMaxWidth: number;
  sharpMaxWidth: number;
  maxDpr: number;
}

/** Buffer renderScale that tracks on-screen size: baseline when zoomed out, rises with zoom×dpr on zoom-in, capped for memory. */
export function resolutionForZoom(zoom: number, viewportWidth: number, dpr: number, caps: ResolutionCaps): number {
  const vw = Math.max(1, viewportWidth);
  const baseline = Math.min(1, caps.baseMaxWidth / vw);
  const ceiling = caps.sharpMaxWidth / vw;
  const desired = zoom * clampZoom(dpr, 1, caps.maxDpr);
  return Math.min(ceiling, Math.max(baseline, desired));
}

/** True once the spread is displayed larger than the capped spread buffer — render the visible viewport tile instead. */
export function shouldSharpTile(zoom: number, viewportWidth: number, dpr: number, caps: ResolutionCaps): boolean {
  return viewportWidth * zoom * clampZoom(dpr, 1, caps.maxDpr) > caps.sharpMaxWidth;
}
